import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { DateTimePicker } from '@mantine/dates';
import { formatDateTimeNSecLabel, formatDateToISO, getCurrentDateTimeISO, getStartOfDay } from '../../../utils/date';
import { Button, Container, Grid, GridCol, Select, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import Header from '../../../components/util/header';
import ChooseEntityModal from '../../components/choose-entity-modal';
import TicketClassification from '../../components/classification';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchHelpdeskEntitiesList } from '../../../../libraries/helpdesk/entities';
import {
  ticketInfoLines,
  ticketInfoPc,
  fetchHelpdeskTicketInfo,
  HeldeskTicketInfoRequests,
  HeldeskTicketInfoUpdateRequests,
  addTicket,
  updateTicket
} from '../../../../libraries/helpdesk/tickets';
import { fetchHelpdeskEquipmentList } from '../../../../libraries/helpdesk/equipments';
import { determineStatus, getCommunicationTypes, getIntTypes, getIssueTypes } from '../../utils';
import { LoginState } from '../../../client-redux/login/reducer';
import TicketLines from './ticket-lines';
import Input from '../../../components/inputs';
import classes from '../../../components/util/date-time-picker.module.scss';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../../context/status-oper-dialog';
import { StatusType } from '../../../../libraries/zps';
import './styles.scss';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const Ticket = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const controller = useRef(new AbortController()).current;
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const eid = query.get('eid');
  const navigate = useNavigate();

  const { mode: modeParam, ticketID, entid, sn } = useParams();

  const [loading, setLoading] = useState(false);

  const [ticket, setTicket] = useState<HeldeskTicketInfoRequests[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedAction, setSelectedAction] = useState<number>(0);
  const [selectedFault, setSelectedFault] = useState<number>(0);
  const [selectedCause, setSelectedCause] = useState<number>(0);
  const [selectedProblem, setSelectedProblem] = useState<number>(0);

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.NEW:
      case MODE.EDIT:
      case MODE.READ:
        return modeParam as MODE;
      default:
        return MODE.READ;
    }
  }, [modeParam]);

  const form = useForm({
    initialValues: {
      agent: { entid: 0, name: '' },
      commChannel: '0',
      contact: '',
      eid: eid ? Number(eid) : 0,
      entid: entid ? Number(entid) : 0,
      sn: sn || '',
      entityName: '',
      intType: '0',
      issueType: '0',
      lines: [] as ticketInfoLines[],
      openDT: mode === MODE.NEW ? getCurrentDateTimeISO() : formatDateTimeNSecLabel(getStartOfDay(), region),
      pc: [{ action: 0, cause: 0, fault: 0, problem: 0 }] as ticketInfoPc[],
      product: '',
      status: determineStatus(0, lang),
      statusDT: '',
      ticketId: 0,
      time: 0,
      urgency: 0,
      description: '',
      comments: ''
    }
  });

  useEffect(() => {
    if (mode === MODE.NEW) {
      form.reset();
    } else if (ticketID) {
      getTicketInfo();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ticketID]);

  useEffect(() => {
    const path = location.pathname;
    const editable = /\/helpdesk\/ticket\/(edit|new)/.test(path);
    setIsEditable(editable);
  }, [location]);

  useEffect(() => {
    if (eid) {
      const equipController = new AbortController();
      setLoading(true);

      fetchHelpdeskEquipmentList(equipController, 0, 12, true, 0, undefined, undefined, Number(eid))
        .then(res => {
          const equipment = res.data.list?.[0];
          if (equipment) {
            form.setValues(prev => ({
              ...prev,
              sn: equipment.sn || '',
              entid: equipment.entity?.entid || 0,
              entityName: equipment.entity?.name || '',
              product: equipment.productCode || '', // if available
              eid: equipment.eid
            }));
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') console.error(err);
        })
        .finally(() => setLoading(false));

      return () => equipController.abort();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, eid]);

  const getTicketInfo = () => {
    if (!login.token) return;
    if (loading) {
      controller.abort();
    }
    setLoading(true);
    fetchHelpdeskTicketInfo(Number(ticketID), controller)
      .then(response => {
        const { data } = response;

        if (isZarphSuccessResponse(response)) {
          const ticketInfo = data.tickets?.[0];

          setTicket(data.tickets || []);

          form.setValues({
            ...ticketInfo,
            commChannel: ticketInfo.commChannel?.toString() || '0',
            intType: ticketInfo.intType?.toString() || '0',
            issueType: ticketInfo.issueType?.toString() || '0',
            status: ticketInfo.status?.toString() || '0',
            lines: ticketInfo.lines || []
          });

          const ticketPCs = ticketInfo.pc?.[0];
          setSelectedAction(ticketPCs?.action || 0);
          setSelectedCause(ticketPCs?.cause || 0);
          setSelectedFault(ticketPCs?.fault || 0);
          setSelectedProblem(ticketPCs?.problem || 0);
        } else {
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (mode === MODE.NEW) {
      form.setValues(prev => ({
        ...prev,
        ticketId: Number(ticketID) || 0,
        eid: eid ? Number(eid) : 0,
        entid: entid ? Number(entid) : 0,
        sn: sn || '',
        openDT: prev.openDT
      }));

      if (entid) {
        fetchHelpdeskEntitiesList(new AbortController(), 0, 1, false, '', Number(entid), '', 0).then(res => {
          const found = res.data.entities?.[0];
          if (found) {
            form.setFieldValue('entityName', found.name || '');
          }
        });
      }
    } else if (ticket?.[0]) {
      const ticketInfo = ticket?.[0];
      form.setValues({
        ...form.values,
        agent: { entid: ticketInfo?.agent?.entid || 0, name: ticketInfo?.agent?.name || '' },
        commChannel: ticketInfo?.commChannel?.toString() || '0',
        contact: ticketInfo?.contact || '',
        eid: ticketInfo?.eid || 0,
        entid: ticketInfo?.entid || 0,
        entityName: ticketInfo?.entityName || '',
        intType: ticketInfo?.intType?.toString() || '0',
        issueType: ticketInfo?.issueType?.toString() || '0',
        lines: ticketInfo?.lines || [],
        openDT: form.values.openDT || getCurrentDateTimeISO(),
        pc: ticketInfo?.pc?.[0]
          ? [ticketInfo.pc[0]]
          : [
              {
                action: selectedAction,
                cause: selectedCause,
                fault: selectedFault,
                problem: selectedProblem
              }
            ],
        product: ticketInfo?.product || '',
        sn: ticketInfo?.sn || '',
        status: determineStatus(ticketInfo?.status || 0, lang),
        statusDT: ticketInfo?.statusDT ? new Date(ticketInfo.statusDT).toISOString() : formatDateToISO(),
        ticketId: ticketInfo?.ticketID || Number(ticketID) || 0,
        time: ticketInfo?.time || 1,
        urgency: ticketInfo?.urgency || 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, eid, entid, sn, ticketID]);

  // Submit form logic
  const submitForm = async (formValues: any) => {
    const ticketLines =
      formValues.lines?.length > 0
        ? formValues.lines
        : [
            {
              description: form.values.description,
              comments: form.values.comments,
              status: form.values.status
            }
          ];

    let request: HeldeskTicketInfoRequests | HeldeskTicketInfoUpdateRequests;

    if (mode === MODE.NEW) {
      request = {
        agent: formValues.agent,
        commChannel: formValues.commChannel,
        contact: formValues.contact,
        eid: formValues.eid || eid,
        entid: formValues.entid || entid,
        entityName: formValues.entityName,
        intType: formValues.intType,
        issueType: formValues.issueType,
        lines: ticketLines,
        openDT: formatDateToISO(formValues.openDT) || formatDateToISO(),
        pc: formValues.pc || [],
        product: formValues.product || '',
        sn: formValues.sn || sn,
        status: formValues.status,
        statusDT: formatDateToISO(formValues.statusDT) || formatDateToISO(),
        system: formValues.system || '',
        ticketID: 0,
        time: formValues.time || 1,
        urgency: formValues.urgency || 0
      };
    } else {
      request = {
        commChannel: formValues.commChannel,
        contact: formValues.contact,
        eid: formValues.eid,
        entid: formValues.entid,
        intType: formValues.intType,
        issueType: formValues.issueType,
        openDT: formValues.openDT,
        pc: formValues.pc || [],
        system: formValues.system,
        ticketID: Number(ticketID)
      };
    }

    const formController = new AbortController();

    try {
      const response =
        mode === MODE.NEW
          ? await addTicket(request as HeldeskTicketInfoRequests, formController)
          : await updateTicket(request as HeldeskTicketInfoUpdateRequests, formController);

      const statusOper = response.data?.statusOper;

      if (!statusOper || statusOper.code !== 0) {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.HELPDESK_EDIT_TICKET_ALERT_ADD_ERROR,
          statusOper
        });
        return;
      }

      const successMessage =
        mode === MODE.NEW
          ? lang.HELPDESK_EDIT_TICKET_ALERT_UPDATED_SUCCESS
          : lang.HELPDESK_EDIT_TICKET_ALERT_UPDATED_SUCCESS;

      showStatusOperDialog({
        type: StatusType.INFO,
        message: successMessage,
        statusOper
      });

      if (mode === MODE.NEW) {
        const newTicketID = response.data?.ticketId;

        if (newTicketID > 0) {
          setTimeout(() => {
            navigate(`/helpdesk/ticket/edit/${newTicketID}`);
          }, 500);
        }
      } else {
        getTicketInfo();
      }
    } catch (error) {
      handleApiError(error, lang.GLOBAL_ERROR);
    }
  };

  const chooseEntid = (entid: number, name?: string) => {
    if (!entid) return;
    form.setFieldValue('entid', entid);
    form.setFieldValue('entityName', name || '');
    toggleChooseEntityModal();
  };

  const handleClassificationChange = (field: string, value: string, index: number) => {
    if (form.values.pc[index]) {
      const updatedPc = [...form.values.pc];
      updatedPc[index] = { ...updatedPc[index], [field]: value };
      form.setFieldValue('pc', updatedPc);
    }
    form.setFieldValue(`${field}_${index}`, value);
  };

  const toggleChooseEntityModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const hideBtn = () => {
    setIsModalOpen(false);
  };

  return (
    <Container fluid classNames={{ root: mode === MODE.EDIT ? 'helpdesk-ticket-edit' : 'helpdesk-ticket-read' }}>
      <ChooseEntityModal lang={lang} isOpen={isModalOpen} hideBtn={hideBtn} chooseEntid={chooseEntid} />

      <Header pageTitle={mode === MODE.NEW ? `${lang.HELPDESK_ADD_TICKET}` : `${lang.GLOBAL_TICKET} ${ticketID}`} />

      {/* Form initialization */}
      <form
        onSubmit={form.onSubmit(values => {
          submitForm(values);
        })}
      >
        <Grid>
          {/* Client */}
          <GridCol span={{ sm: 12, md: 4, lg: 4 }}>
            <Input
              label={lang.GLOBAL_CLIENT}
              value={form.values.entid ? `${form.values.entid} - ${form.values.entityName ?? ''}` : ''}
              disabled={!isEditable}
              isEditable={isEditable}
              search={toggleChooseEntityModal}
            />
          </GridCol>

          {/* Contact */}
          <GridCol span={{ sm: 12, md: 8, lg: 8 }}>
            <TextInput
              label={lang.GLOBAL_CONTACT}
              value={form.values.contact}
              disabled={!isEditable}
              {...form.getInputProps('contact')}
            />
          </GridCol>

          {/* Communication channel */}
          <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
            <Select
              label={lang.HELPDESK_TICKET_UPDATE_COMMUNICATION_CHANNEL}
              data={getCommunicationTypes(lang)}
              value={form.values.commChannel.toString()}
              disabled={!isEditable}
              {...form.getInputProps('commChannel')}
            />
          </GridCol>

          {/* EID */}
          <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
            <TextInput
              label="EID"
              value={form.values.eid}
              {...form.getInputProps('eid')}
              disabled={!isEditable}
              onInput={e => {
                const target = e.currentTarget;
                //Allow only numbers
                target.value = target.value.replace(/[^0-9]/g, '');
              }}
            />
          </GridCol>

          {/* Serial number */}
          <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
            <TextInput
              label={lang.EQUIPMENT_SERIAL_NUMBER}
              value={form.values.sn}
              disabled={!isEditable}
              {...form.getInputProps('sn')}
            />
          </GridCol>

          {/* Issue Types*/}
          <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
            <Select
              label={lang.HELPDESK_TICKET_UPDATE_ISSUE_TYPES}
              data={getIssueTypes(lang)}
              value={form.values.issueType.toString()}
              disabled={!isEditable}
              {...form.getInputProps('issueType')}
            />
          </GridCol>

          {/* Open date */}
          <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
            <DateTimePicker
              label={lang.HELPDESK_TICKET_OPEN_DT}
              value={form.values.openDT ? new Date(form.values.openDT) : null}
              onChange={date => form.setFieldValue('openDT', date ? formatDateToISO(date) : '')}
              disabled={!isEditable}
              clearable
              valueFormat="YYYY/MM/DD HH:mm"
              timePickerProps={{
                withDropdown: true,
                popoverProps: { withinPortal: false },
                format: '24h'
              }}
            />
          </GridCol>

          {mode !== MODE.NEW && (
            <>
              {/* Last update */}
              <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
                <DateTimePicker
                  classNames={{
                    day: classes.day
                  }}
                  label={lang.HELPDESK_TICKET_UPDATE_LAST_UPDATE}
                  value={form.values.statusDT ? new Date(form.values.statusDT) : null}
                  onChange={date => form.setFieldValue('statusDT', date ? formatDateToISO(date) : '')}
                  disabled
                  valueFormat="YYYY/MM/DD HH:mm"
                  timePickerProps={{
                    withDropdown: true,
                    popoverProps: { withinPortal: false },
                    format: '24h',
                    classNames: {
                      input: classes.timeInput,
                      field: classes.timeField,
                      control: classes.timeControl
                    }
                  }}
                />
              </GridCol>
              {/* Last status */}
              <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
                <TextInput
                  label={lang.HELPDESK_TICKET_UPDATE_LAST_STATUS}
                  value={form.values.status}
                  disabled
                  {...form.getInputProps('status')}
                />
              </GridCol>
              {/* Int type */}
              <GridCol span={{ sm: 12, md: 3, lg: 3 }}>
                <Select
                  label={lang.HELPDESK_TICKET_UPDATE_INT_TYPE}
                  data={getIntTypes(lang)}
                  value={form.values.intType.toString()}
                  disabled={!isEditable}
                  {...form.getInputProps('intType')}
                />
              </GridCol>
              {/* Classifications */}
              <TicketClassification onSelectionChange={handleClassificationChange} form={form} />
            </>
          )}
        </Grid>

        {mode === MODE.NEW && (
          <Grid className="description-comment">
            <GridCol span={{ sm: 12, md: 6, lg: 6 }}>
              <Textarea
                label={lang.EQUIPMENT_DESCRIPTION}
                value={form.values.description}
                disabled={!isEditable}
                {...form.getInputProps('description')}
              />
            </GridCol>

            <GridCol span={{ sm: 12, md: 6, lg: 6 }}>
              <Textarea
                label={lang.GLOBAL_COMMENTS}
                value={form.values.comments || '-'}
                disabled={!isEditable}
                {...form.getInputProps('comments')}
              />
            </GridCol>
          </Grid>
        )}
      </form>

      {/* Submit button */}
      {mode !== MODE.READ && (
        <Button
          color="#538539"
          radius="md"
          onClick={e => {
            e.preventDefault();
            submitForm(form.values);
          }}
        >
          {lang.GLOBAL_SAVE_BUTTON}
        </Button>
      )}

      <TicketLines
        ticketData={Number(ticketID)}
        lines={form.values.lines}
        loading={loading && (!form.values.lines || !ticket)}
        noResults={!ticket.length}
        mode={mode}
        determineStatus={determineStatus}
        onReload={getTicketInfo}
      />
    </Container>
  );
};

export default Ticket;
